import React, { useState, useEffect, useRef } from 'react';
import { useQuery, gql } from '@apollo/client';
import { Link } from 'react-router-dom';

const GET_CATEGORIES = gql`
    query getMegaMenu {
        categoryList {
            id
            uid
            name
            children {
                id
                uid
                include_in_menu
                name
                position
                url_path
                url_suffix
                children {
                    id
                    uid
                    include_in_menu
                    name
                    position
                    url_path
                    url_suffix
                    children {
                        id
                        uid
                        include_in_menu
                        name
                        position
                        url_path
                        url_suffix
                    }
                }
            }
        }
    }
`;

const MENU_CACHE_KEY = 'simple_shop_menu_cache';
const CACHE_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours

const Menu = ({ isMobile = false, isOpen = false, onClose = () => {} }) => {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [cachedData, setCachedData] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [dropdownPosition, setDropdownPosition] = useState({});
  const menuRef = useRef(null);
  const timeoutRef = useRef(null);
  const dropdownRefs = useRef({});

  // Check localStorage cache first
  useEffect(() => {
    const cachedMenu = localStorage.getItem(MENU_CACHE_KEY);
    if (cachedMenu) {
      const parsed = JSON.parse(cachedMenu);
      if (Date.now() - parsed.timestamp < CACHE_EXPIRY) {
        setCachedData(parsed.data);
      } else {
        localStorage.removeItem(MENU_CACHE_KEY);
      }
    }
  }, []);

  const { loading, error, data } = useQuery(GET_CATEGORIES, {
    skip: cachedData !== null,
    onCompleted: (data) => {
      // Cache the data
      const cacheData = {
        data,
        timestamp: Date.now()
      };
      localStorage.setItem(MENU_CACHE_KEY, JSON.stringify(cacheData));
    }
  });

  // Use cached data if available, otherwise use query data
  const menuData = cachedData || data;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  if (loading && !cachedData) return null;
  if (error) return null; // Fail silently for menu

  if (!menuData?.categoryList) return null;

  // Get categories from root level
  const rootCategories = menuData.categoryList || [];

  // Get main menu categories (first level children that are in menu)
  const mainCategories = rootCategories
    .flatMap(root => root.children || [])
    .filter(cat => cat.include_in_menu)
    .sort((a, b) => (a.position || 0) - (b.position || 0));

  const handleMouseEnter = (categoryId, event) => {
    if (!isMobile) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      setActiveDropdown(categoryId);

      // Calculate dropdown position after a small delay to ensure it's rendered
      setTimeout(() => {
        const menuItem = event?.currentTarget;
        if (!menuItem) return;

        const rect = menuItem.getBoundingClientRect();
        const dropdownWidth = 800;
        const viewportWidth = window.innerWidth;
        const menuItemRight = rect.right;
        const menuItemLeft = rect.left;

        let position = { left: '50%', transform: 'translateX(-50%)' };

        // If menu item is in right 40% of screen, align dropdown to right
        if (menuItemRight > viewportWidth * 0.6) {
          position = { right: '0', transform: 'none' };
        }
        // If menu item is in left 40% of screen, align dropdown to left
        else if (menuItemLeft < viewportWidth * 0.4) {
          position = { left: '0', transform: 'none' };
        }

        setDropdownPosition(prev => ({ ...prev, [categoryId]: position }));
      }, 10);
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile) {
      timeoutRef.current = setTimeout(() => {
        setActiveDropdown(null);
      }, 150); // 150ms delay
    }
  };

  const toggleCategory = (categoryId) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  // Mobile Menu
  if (isMobile) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: isOpen ? 0 : '-100%',
        width: '100%',
        height: '100vh',
        backgroundColor: 'white',
        zIndex: 2000,
        transition: 'left 0.3s ease-in-out',
        overflowY: 'auto'
      }}>
        {/* Mobile Menu Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '1rem',
          borderBottom: '1px solid #e5e7eb',
          backgroundColor: '#7c3aed'
        }}>
          <h2 style={{ color: 'white', margin: 0, fontSize: '1.25rem' }}>Menu</h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'white',
              fontSize: '1.5rem',
              cursor: 'pointer',
              padding: '0.5rem'
            }}
          >
            ✕
          </button>
        </div>

        {/* Mobile Menu Content */}
        <div style={{ padding: '1rem' }}>
          <Link
            to="/"
            style={{
              display: 'block',
              padding: '0.75rem',
              color: '#374151',
              textDecoration: 'none',
              borderBottom: '1px solid #e5e7eb',
              fontWeight: '500'
            }}
            onClick={onClose}
          >
            Home
          </Link>

          {mainCategories.map((category) => (
            <div key={category.id}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '0.75rem',
                borderBottom: '1px solid #e5e7eb'
              }}>
                <Link
                  to={`/${category.url_path}${category.url_suffix || ''}`}
                  style={{
                    color: '#7c3aed',
                    textDecoration: 'none',
                    fontWeight: '600',
                    flex: 1
                  }}
                  onClick={onClose}
                >
                  {category.name}
                </Link>
                {category.children?.length > 0 && (
                  <button
                    onClick={() => toggleCategory(category.id)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#7c3aed',
                      fontSize: '1.2rem',
                      cursor: 'pointer',
                      padding: '0.25rem'
                    }}
                  >
                    {expandedCategories.has(category.id) ? '−' : '+'}
                  </button>
                )}
              </div>

              {/* Mobile Subcategories */}
              {expandedCategories.has(category.id) && category.children?.length > 0 && (
                <div style={{ paddingLeft: '1rem', backgroundColor: '#f9fafb' }}>
                  {category.children
                    .filter(subCat => subCat.include_in_menu)
                    .sort((a, b) => (a.position || 0) - (b.position || 0))
                    .map((subCategory) => (
                      <div key={subCategory.id}>
                        <Link
                          to={`/${subCategory.url_path}${subCategory.url_suffix || ''}`}
                          style={{
                            display: 'block',
                            padding: '0.5rem 0.75rem',
                            color: '#6b7280',
                            textDecoration: 'none',
                            borderBottom: '1px solid #e5e7eb',
                            fontSize: '0.9rem'
                          }}
                          onClick={onClose}
                        >
                          {subCategory.name}
                        </Link>

                        {/* Mobile Sub-subcategories */}
                        {subCategory.children?.length > 0 && (
                          <div style={{ paddingLeft: '1rem', backgroundColor: '#f3f4f6' }}>
                            {subCategory.children
                              .filter(subSub => subSub.include_in_menu)
                              .sort((a, b) => (a.position || 0) - (b.position || 0))
                              .slice(0, 8)
                              .map((subSubCategory) => (
                                <Link
                                  key={subSubCategory.id}
                                  to={`/${subSubCategory.url_path}${subSubCategory.url_suffix || ''}`}
                                  style={{
                                    display: 'block',
                                    padding: '0.4rem 0.75rem',
                                    color: '#9ca3af',
                                    textDecoration: 'none',
                                    fontSize: '0.8rem'
                                  }}
                                  onClick={onClose}
                                >
                                  {subSubCategory.name}
                                </Link>
                              ))}
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Desktop Menu
  return (
    <nav ref={menuRef} style={{
      position: 'relative',
      display: 'flex',
      justifyContent: 'center',
      maxWidth: '1400px',
      margin: '0 auto',
      padding: '1rem 0'
    }}>
      <ul style={{
        display: 'flex',
        listStyle: 'none',
        margin: 0,
        padding: 0,
        gap: '3rem',
        alignItems: 'center',
        position: 'relative'
      }}>
        {mainCategories.map((category, index) => {
          // Determine dropdown alignment based on position in menu
          const totalItems = mainCategories.length;
          const isRightSide = index >= totalItems - 2; // Last 2 items
          const isLeftSide = index <= 1; // First 2 items

          return (
          <li
            key={category.id}
            style={{ position: 'relative' }}
            onMouseEnter={(e) => handleMouseEnter(category.id, e)}
            onMouseLeave={handleMouseLeave}
          >
            <Link
              to={`/${category.url_path}${category.url_suffix || ''}`}
              style={{
                textDecoration: 'none',
                color: 'white',
                fontWeight: '500',
                fontSize: '0.95rem',
                padding: '0.75rem 1.25rem',
                display: 'block',
                transition: 'all 0.2s',
                borderRadius: '6px',
                whiteSpace: 'nowrap',
                textAlign: 'center'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'rgba(255,255,255,0.15)';
                e.target.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.transform = 'translateY(0)';
              }}
            >
              {category.name}
            </Link>

            {/* Dropdown Menu */}
            {activeDropdown === category.id && category.children?.length > 0 && (
              <div
                style={{
                  position: 'fixed',
                  top: '140px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '95vw',
                  maxWidth: '1600px',
                  backgroundColor: 'white',
                  boxShadow: '0 12px 30px rgba(0,0,0,0.18)',
                  borderRadius: '12px',
                  padding: '1.5rem',
                  zIndex: 1000,
                  display: 'grid',
                  gridTemplateColumns: `repeat(auto-fit, minmax(170px, 1fr))`,
                  gap: '1.5rem',
                  border: '1px solid #e5e7eb',
                  maxHeight: '70vh',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  maxHeight: '70vh',
                  overflowY: 'auto'
                }}
                onMouseEnter={(e) => handleMouseEnter(category.id, e)}
                onMouseLeave={handleMouseLeave}
              >
                {category.children
                  .filter(subCat => subCat.include_in_menu)
                  .sort((a, b) => (a.position || 0) - (b.position || 0))
                  .map((subCategory) => (
                    <div key={subCategory.id} style={{ minWidth: '200px' }}>
                      <Link
                        to={`/${subCategory.url_path}${subCategory.url_suffix || ''}`}
                        style={{
                          display: 'block',
                          textDecoration: 'none',
                          color: '#7c3aed',
                          fontWeight: '600',
                          fontSize: '1.05rem',
                          marginBottom: '1rem',
                          padding: '0.5rem 0 0.75rem 0',
                          borderBottom: '2px solid #e5e7eb',
                          textAlign: 'center',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.color = '#5b21b6';
                          e.target.style.borderBottomColor = '#7c3aed';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.color = '#7c3aed';
                          e.target.style.borderBottomColor = '#e5e7eb';
                        }}
                      >
                        {subCategory.name}
                      </Link>

                      {/* Sub-subcategories */}
                      {subCategory.children?.length > 0 && (
                        <ul style={{
                          listStyle: 'none',
                          margin: 0,
                          padding: 0,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '0.25rem'
                        }}>
                          {subCategory.children
                            .filter(subSub => subSub.include_in_menu)
                            .sort((a, b) => (a.position || 0) - (b.position || 0))
                            .slice(0, 8) // Limit to avoid too long lists
                            .map((subSubCategory) => (
                              <li key={subSubCategory.id}>
                                <Link
                                  to={`/${subSubCategory.url_path}${subSubCategory.url_suffix || ''}`}
                                  style={{
                                    textDecoration: 'none',
                                    color: '#6b7280',
                                    fontSize: '0.85rem',
                                    padding: '0.4rem 0.75rem',
                                    display: 'block',
                                    borderRadius: '6px',
                                    transition: 'all 0.2s',
                                    textAlign: 'center'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = '#f8fafc';
                                    e.target.style.color = '#374151';
                                    e.target.style.transform = 'translateX(2px)';
                                    e.target.style.borderLeft = '3px solid #7c3aed';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = 'transparent';
                                    e.target.style.color = '#6b7280';
                                    e.target.style.transform = 'translateX(0)';
                                    e.target.style.borderLeft = 'none';
                                  }}
                                >
                                  {subSubCategory.name}
                                </Link>
                              </li>
                            ))}
                        </ul>
                      )}
                    </div>
                  ))}
              </div>
            )}
          </li>
          );
        })}
      </ul>
    </nav>
  );
};

export default Menu;